import { ReactElement, useEffect } from 'react'
import { NetworkStatus, useMutation, useQuery } from '@apollo/client'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useNavigate, useParams } from 'react-router-dom'

import { Content, ContentHeader } from 'components/v2/Content'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { INewPassengerPropertyForm } from 'features/PassengerProperties/components/NewPassengerProperty/types'
import { NewPassengerProperty } from 'features/PassengerProperties/components/NewPassengerProperty'
import { canWrite } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'
import Card from '@10xLabs/react-web/components/Card'
import { Loader } from '@10xLabs/react-web/components/Loader'
import Heading from 'components/v2/Heading'
import { Button } from 'components/v2/Button'
import EmptyState from 'components/v2/EmptyState'
import { EDIT_PASSENGER_PROPERTY } from 'features/PassengerProperties/mutations-legacy'
import { GET_PASSENGER_PROPERTY } from 'features/PassengerProperties/queries-legacy'
import { GTChangePassengerPropertyInput, GTLanguage, GTPassengerProperty } from 'gql/types.generated'
import { getDifference } from 'utils/object'

const messages = defineMessages({
  helmet: {
    id: 'passengerProperties.new.helmet',
    defaultMessage: 'Edit Passenger Property | Nexbus Admin'
  }
})

const EditPassengerPropertyView = (): ReactElement => {
  const navigate = useNavigate()
  const intl = useIntl()
  const login = useLogin()
  const { passengerPropertyId } = useParams()

  let content = <></>

  const [editPassengerProperty, { data, loading: mutationLoading, error: mutationError }] =
    useMutation(EDIT_PASSENGER_PROPERTY)
  const {
    loading: queryLoading,
    error: queryError,
    data: queryData,
    refetch,
    networkStatus
  } = useQuery<{ passengerProperty: GTPassengerProperty }>(GET_PASSENGER_PROPERTY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        id: passengerPropertyId
      }
    }
  })

  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  useEffect(() => {
    if (data) {
      navigate('/passenger-properties')
    }
  }, [data])

  const getEditPassengerVariables = (values: INewPassengerPropertyForm): GTChangePassengerPropertyInput => {
    const previousData = {
      label: queryData?.passengerProperty.label ?? [],
      abbreviation: queryData?.passengerProperty.abbreviation ?? [],
      description: queryData?.passengerProperty.description ?? [],
      departureDateRanges:
        queryData?.passengerProperty.departureDateRanges.map((i) => ({
          startAt: new Date(i.startAt).toJSON(),
          endAt: new Date(i.endAt).toJSON()
        })) ?? [],
      searchDateRanges:
        queryData?.passengerProperty.searchDateRanges.map((i) => ({
          startAt: new Date(i.startAt).toJSON(),
          endAt: new Date(i.endAt).toJSON()
        })) ?? [],
      required: queryData?.passengerProperty.required ?? false,
      settings:
        queryData?.passengerProperty.settings.map((i) => ({
          salesChannelIDs: i.salesChannelsV2.map(({ id }) => id),
          discount: i.discount,
          passengerTypeIDs: i.passengerTypes.map((j) => j?.id ?? '')
        })) ?? []
    }
    const newData = {
      label: [
        {
          language: GTLanguage.Es,
          text: values.esLabel
        },
        {
          language: GTLanguage.En,
          text: values.enLabel
        }
      ],
      abbreviation: [
        {
          language: GTLanguage.Es,
          text: values.esAbbreviation
        },
        {
          language: GTLanguage.En,
          text: values.enAbbreviation
        }
      ],
      description: values.enDescription
        ? [
            {
              language: GTLanguage.Es,
              text: values.esDescription
            },
            {
              language: GTLanguage.En,
              text: values.enDescription
            }
          ]
        : [],
      departureDateRanges: values.departureDateRanges.map((i) => ({
        startAt: i.startAt.toJSON(),
        endAt: i.endAt.toJSON()
      })),
      searchDateRanges: values.searchDateRanges.map((i) => ({
        startAt: i.startAt.toJSON(),
        endAt: i.endAt.toJSON()
      })),
      required: values.required,
      settings: [
        {
          discount: Number(values.settings.discount),
          salesChannelIDs: values.settings.salesChannels.map(({ id }) => id),
          passengerTypeIDs: values.passengerTypes.map((passengerType) => passengerType?.id)
        }
      ]
    }

    const payload = getDifference(newData, previousData)
    return {
      id: passengerPropertyId as string,
      ...payload
    }
  }
  const handleSubmit = (values: INewPassengerPropertyForm): void => {
    editPassengerProperty({
      variables: {
        input: getEditPassengerVariables(values)
      }
    })
  }

  if (!canWrite(FEATURES.PASSENGER_PROPERTIES, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.PASSENGER_PROPERTIES} />
  }
  if (networkStatus === NetworkStatus.refetch || queryLoading) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <Loader />
        </EmptyState.Container>
      </Card.Content>
    )
  } else if (queryError) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <EmptyState.Description>
            <FormattedMessage
              id="passengerProperty.edit.error.description"
              defaultMessage="There was an error while loading the data"
            />
          </EmptyState.Description>

          <EmptyState.Action>
            <Button
              onClick={(): void => {
                refetch()
              }}
              kind="primary"
            >
              <FormattedMessage id="passengerProperty.edit.error.action" defaultMessage="Reload" />
            </Button>
          </EmptyState.Action>
        </EmptyState.Container>
      </Card.Content>
    )
  } else {
    content = (
      <NewPassengerProperty
        onSubmit={handleSubmit}
        passengerPropertyData={queryData?.passengerProperty}
        loading={mutationLoading}
        error={mutationError}
        isEdit
      />
    )
  }

  return (
    <Content hasMaxWidth>
      <ContentHeader>
        <Heading.H3>
          <FormattedMessage id="passengerProperty.edit.title" defaultMessage="Edit Passenger Property" />
        </Heading.H3>
      </ContentHeader>

      <section>
        <Card.Container>{content}</Card.Container>
      </section>
    </Content>
  )
}

export default withLastUrl(EditPassengerPropertyView)
